// src/services/apaleo/city-tax-recalculate.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { ReservationModel, AutomationResult, ToolUser, LogFn, OnLogFn, OnProgressFn } from './types';
import type { ApaleoClient } from './client-contract';
import { CITY_TAX_TOOL_PROPERTY_ID, errorMessage } from './config';

const part: ThisType<ApaleoClient> = {
async recalculateCityTaxes({
  dateFrom = null,
  dateTo = null,
  reservationId = null,
  sources = null,
  createdFrom = null,
  createdTo = null,
  dryRun = true,
  user = null,
  onProgress = null,
  onLog = null,
}: {
  dateFrom?: string | null;
  dateTo?: string | null;
  reservationId?: string | null;
  sources?: string[] | string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  dryRun?: boolean;
  user?: ToolUser | null;
  onProgress?: OnProgressFn | null;
  onLog?: OnLogFn | null;
} = {}): Promise<AutomationResult> {
  const singleMode =
    typeof reservationId === "string" && reservationId.trim().length > 0;
  const singleReservationId = singleMode ? (reservationId as string).trim() : null;

  // Shared audit-filter normalization & fail-closed validation
  // (booking-source allow-list + creation-date window). Invalid input
  // throws HERE, before any fetch, so a typo can never silently turn
  // into a full-table mutation — see _normalizeAuditFilters.
  const { sourceFilter, createdFromTs, createdToTs } =
    this._normalizeAuditFilters(sources, createdFrom, createdTo);
  // Used in log metadata to identify the operator for audit purposes.
  const operatorLabel =
    (user && (user.name || user.id || user.email))
      ? `${user.name || user.id || user.email}`
      : "system";
  const log: LogFn = (level, message) => {
    const decorated = `[recalcCityTax] (${operatorLabel}) ${message}`;
    if (typeof onLog === "function") {
      try { onLog({ level, message: decorated }); } catch { /* ignore listener errors */ }
    }
  };

  const result: AutomationResult = {
    dryRun: !!dryRun,
    dateFrom,
    dateTo,
    mode: singleMode ? "single" : "range",
    reservationId: singleReservationId,
    sourceFilter,
    createdFrom,
    createdTo,
    processed: 0,
    skipped: 0,
    failed: 0,
    results: [],
  };

  if (singleMode) {
    if (!dateFrom || !dateTo) {
      // For single mode the date range is not strictly required, but we
      // still capture the operator's intent for the audit log.
      dateFrom = dateFrom || "";
      dateTo = dateTo || "";
    }
  } else {
    if (!dateFrom || !dateTo) {
      throw new Error("recalculateCityTaxes: dateFrom and dateTo are required");
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      throw new Error("recalculateCityTaxes: dateFrom must be <= dateTo");
    }
  }

  let reservations: ReservationModel[];
  if (singleMode) {
    log("log", `Single-reservation mode: fetching ${singleReservationId} (dryRun=${dryRun})`);
    const single = await this.fetchReservationById(singleReservationId || "");
    if (!single) {
      throw new Error(`recalculateCityTaxes: reservation ${singleReservationId} not found`);
    }
    reservations = [single];
  } else {
    log("log", `Fetching reservations with arrival in [${dateFrom}, ${dateTo}] (dryRun=${dryRun})`);

    // Use the existing helper; the "Arrival" filter selects by arrival date.
    // We ask the API to also return `totalAmount` so the dry-run preview
    // can show the original (imported) total the operator expects to be
    // preserved after the recalculation.
    // BERLIN-only scoping (fetch level): only the Berlin property is
    // scanned — the 7.5% city tax does not apply to the other properties.
    reservations = await this.fetchReservations(
      dateFrom,
      dateTo,
      "Arrival",
      "timeSlices,totalAmount,totalGrossAmount,totalNetAmount",
      "",
      CITY_TAX_TOOL_PROPERTY_ID,
    );
  }

  if (!reservations || reservations.length === 0) {
    log("warn", "No reservations in the selected period.");
    return result;
  }

  const total = reservations.length;
  log("log", `Found ${total} reservation(s) in the period.`);

  for (let i = 0; i < reservations.length; i++) {
    const res = reservations[i];
    const guestName = `${res.primaryGuest?.firstName || ""} ${res.primaryGuest?.lastName || ""}`.trim() || res.id;
    // Two DIFFERENT identities — do not conflate them:
    //  - `source` → OTA name ("Booking.com", "HRS", "Expedia", "" for direct).
    //    Drives the audit-filter matching and the results-table column.
    //  - `channelCode` → import channel ("Direct" | "ChannelManager").
    //    Drives the ChannelManager gate below.
    const source = (res.source || "").trim();
    const channelCode = (res.channelCode || "").trim();
    // Display label: prefer the human-readable OTA name.
    const channel = source || channelCode || "Direct";

    // Display-safe property id for the skip reason log below.
    const propertyId = (res.property?.id || "").trim();

    // Reservation total: prefer the expanded `totalAmount` (the
    // imported total that already incorrectly includes the city tax).
    // The script's job is to preserve this imported total after
    // recalculation. Fall back to `totalGrossAmount` or the time-slice
    // sum if `totalAmount` is missing from the expanded payload.
    const currency =
      res.totalAmount?.currency ||
      res.totalGrossAmount?.currency ||
      res.totalNetAmount?.currency ||
      "EUR";
    const expandedTotal: number | null =
      res.totalAmount?.amount != null
        ? Number(res.totalAmount.amount)
        : res.totalGrossAmount?.amount != null
          ? Number(res.totalGrossAmount.amount)
          : res.totalNetAmount?.amount != null
            ? Number(res.totalNetAmount.amount)
            : null;

    if (typeof onProgress === "function") {
      try { onProgress({ processed: i, total, current: res.id || null }); } catch { /* noop */ }
    }

    // 0. Property gate (BERLIN-only tool). Row-level backstop for the
    // fetch-level scoping above — single-reservation mode fetches by ID
    // without a property filter, so a non-Berlin ID must be rejected here.
    // Runs before the audit filters; fail-closed on unknown property.
    if (propertyId !== CITY_TAX_TOOL_PROPERTY_ID) {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "property_not_berlin",
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
      });
      log("log", `Skip ${res.id} (${guestName}): property='${propertyId || "unknown"}' — tool is BERLIN-only`);
      continue;
    }

    // 0. Booking-source filter (audit-scoping; runs BEFORE the
    //    ChannelManager gate so skipped rows explain themselves).
    if (sourceFilter && !sourceFilter.includes(source)) {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "source_not_selected",
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
      });
      log("log", `Skip ${res.id} (${guestName}): source='${source}' not in [${sourceFilter.join(", ")}]`);
      continue;
    }

    // 0b. Creation-date window (audit-scoping). Inclusive local-day
    //     bounds compared against the reservation's `created` timestamp.
    //     Reservations without a parseable `created` are skipped —
    //     fail-closed: an un-dateable row must never slip into a real
    //     amend run just because its timestamp is malformed.
    if (createdFromTs || createdToTs) {
      const createdTs = res.created ? Date.parse(res.created) : NaN;
      if (Number.isNaN(createdTs)) {
        result.skipped++;
        result.results.push({
          reservationId: res.id,
          guestName,
          channel,
          source,
          status: "skipped",
          reason: "created_missing_or_invalid",
          originalTotal: expandedTotal,
          newTotal: expandedTotal,
          currency,
        });
        log("warn", `Skip ${res.id} (${guestName}): created timestamp missing or invalid`);
        continue;
      }
      const createdLocal = new Date(createdTs);
      // Local calendar-day start of the reservation's creation timestamp.
      const createdDayStart = new Date(
        createdLocal.getFullYear(),
        createdLocal.getMonth(),
        createdLocal.getDate(),
      ).getTime();
      if (createdFromTs && createdDayStart < createdFromTs.getTime()) {
        result.skipped++;
        result.results.push({
          reservationId: res.id,
          guestName,
          channel,
          source,
          status: "skipped",
          reason: "created_before_window",
          originalTotal: expandedTotal,
          newTotal: expandedTotal,
          currency,
        });
        log("log", `Skip ${res.id} (${guestName}): created ${res.created} before ${createdFrom}`);
        continue;
      }
      if (createdToTs && createdDayStart > createdToTs.getTime()) {
        result.skipped++;
        result.results.push({
          reservationId: res.id,
          guestName,
          channel,
          source,
          status: "skipped",
          reason: "created_after_window",
          originalTotal: expandedTotal,
          newTotal: expandedTotal,
          currency,
        });
        log("log", `Skip ${res.id} (${guestName}): created ${res.created} after ${createdTo}`);
        continue;
      }
    }

    // 0c. Status gate — ONLY Confirmed reservations are touched. Canceled
    //     reservations still appear in arrival-window fetches, and amending
    //     a canceled folio would resurrect charges on a dead booking.
    //     Runs before the ChannelManager gate; fail-closed on missing
    //     status. Single-reservation mode fetches by ID with no status
    //     filter, so this row gate is the only protection there.
    if (res.status !== "Confirmed") {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "status_not_confirmed",
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
      });
      log("log", `Skip ${res.id} (${guestName}): status='${res.status || "unknown"}' — only Confirmed reservations are processed`);
      continue;
    }

    // 1. Channel filter (only ChannelManager reservations are touched)
    if (channelCode !== "ChannelManager") {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "channel_not_channelmanager",
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
      });
      log("log", `Skip ${res.id} (${guestName}): channel=${channel}`);
      continue;
    }

    // 1b. Idempotency gate — reservations that a previous run of EITHER
    //     automation tool (Ortstaxe neu berechnen / HRS-Rates aufteilen)
    //     already stamped via _appendReservationComment are skipped so the
    //     amend never executes twice. `comment` is a base reservation
    //     field, so it is already present on the fetched object — no
    //     extra API call per row.
    if (this._hasAutomationMarker(res.comment)) {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "already_processed_by_automation",
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
      });
      log("log", `Skip ${res.id} (${guestName}): automation comment already present — not running again`);
      continue;
    }

    // 2. Validate time slices
    const timeSlices = res.timeSlices || [];
    if (timeSlices.length === 0) {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "no_time_slices",
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
      });
      log("warn", `Skip ${res.id} (${guestName}): no time slices`);
      continue;
    }

    // Per-night price used for the city-tax calculation.
    // Apaleo returns the time slice under several equivalent fields
    // depending on the expand params; the canonical ones (per real payload)
    // are `baseAmount.grossAmount` (accommodation only) and
    // `totalGrossAmount.amount` (accommodation + extras). We prefer
    // `baseAmount` so breakfast / extras don't inflate the city-tax base.
    const slicePrices: number[] = timeSlices.map((ts) => {
      const raw =
        ts?.baseAmount?.grossAmount ??
        ts?.totalAmount?.amount ??
        ts?.totalGrossAmount?.amount ??
        null;
      if (raw == null) return NaN;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : NaN;
    });
    const sumSlicePrices = slicePrices.reduce(
      (acc, p) => acc + (Number.isFinite(p) ? p : 0),
      0,
    );
    const originalTotal = expandedTotal != null ? expandedTotal : sumSlicePrices;

    if (slicePrices.some((p) => p === null || Number.isNaN(p))) {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason: "invalid_time_slice_price",
        originalTotal,
        newTotal: originalTotal,
        currency,
      });
      log("warn", `Skip ${res.id} (${guestName}): invalid time slice price(s)`);
      continue;
    }

    // 3. Projected per-night breakdown for the dry-run preview.
    //    Business context: ChannelManager bookings are imported with an
    //    accommodation cost that already (wrongly) includes the city tax
    //    (e.g. IWYWIVXK-1: imported total 296.52 EUR, but the per-night
    //    accommodation 85.79/91.80/99.52 sums to 277.11 which IS the
    //    "true" accommodation; the city tax was double-counted on import).
    //    The amend MUST reduce the per-night accommodation so Apaleo
    //    recalculates the city tax server-side (city tax cannot be
    //    amended in isolation; touching the timeslices is the only way
    //    to trigger a tax recalculation). The new total is therefore
    //    the "true accommodation" (~277.11 for IWYWIVXK-1), not the
    //    imported total.
    //    Per-night math (Berlin model, Apaleo-verified 2026-10-02: the city
    //    tax line carries 7% VAT itself, so
    //    gross = net*1.07 * (1 + 0.075) = net * 1.07 * 1.075):
    //      new_accom     = orig_accom / 1.075
    //      new_city_tax  = round(new_accom * 0.075, 2)   (gross, VAT-inclusive)
    //      new_total     = sum(new_accom) + sum(new_city_tax)
    //                    = orig_total  (up to per-night rounding)
    //    Per-night rounding can leave a ±1 cent drift on the projected
    //    total relative to orig_total / DIVISOR. We absorb that drift by
    //    bumping the LAST night's accommodation by the residual cents,
    //    using `originalTotal / DIVISOR` (= originalTotal / 1.075, the
    //    "true" reduced total) as the target. We intentionally do NOT
    //    recompute the last night's
    //    tax from the bumped accommodation (the second `.toFixed(2)`
    //    rounding would push the delta beyond the residual).
    const DIVISOR = 1.075; // gross = net * 1.07 (VAT) * 1.075 (city tax)
    const newPerNightPrices = slicePrices.map((g) =>
      Number((g / DIVISOR).toFixed(2)),
    );
    // Projected auto city tax per night (mirrors Apaleo's formula; the tax
    // line itself carries 7% VAT, so the projected value is VAT-inclusive).
    const projectedCityTaxPerNight = newPerNightPrices.map((accom) =>
      Number((accom * 0.075).toFixed(2)),
    );
    // NOTE: residual absorption is intentionally NOT applied here.
    // The two-step amend pattern (step 4 below) handles the correction
    // after Apaleo's server-side rounding. Applying the residual bump
    // before the first amend would cause Apaleo to store the bumped
    // value (e.g. 99.52) and the drift check would pass, leaving the
    // total at 296.53 instead of 296.52.
    const totalNewAccommodation = newPerNightPrices.reduce((a, b) => a + b, 0);
    const totalProjectedCityTax = projectedCityTaxPerNight.reduce((a, b) => a + b, 0);
    const projectedNewTotal = Number(
      (totalNewAccommodation + totalProjectedCityTax).toFixed(2),
    );

    if (dryRun) {
      result.processed++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "dryRun",
        originalTotal,
        newTotal: projectedNewTotal,
        nights: timeSlices.length,
        perNightGross: slicePrices,
        perNightAccommodation: newPerNightPrices,
        perNightCityTax: projectedCityTaxPerNight,
        totalAccommodation: Number(totalNewAccommodation.toFixed(2)),
        totalCityTax: Number(totalProjectedCityTax.toFixed(2)),
        currency,
      });
      const deltaCents = Math.round(
        (projectedNewTotal - originalTotal) * 100,
      );
      const deltaStr =
        deltaCents > 0
          ? `+${(deltaCents / 100).toFixed(2)}`
          : (deltaCents / 100).toFixed(2);
      log(
        "log",
        `Dry-run ${res.id} (${guestName}): ${timeSlices.length} night(s), ` +
        `accommodation ${totalNewAccommodation.toFixed(2)} + projected city tax ${totalProjectedCityTax.toFixed(2)} ` +
        `= ${projectedNewTotal.toFixed(2)} ${currency} ` +
        `(Δ ${deltaStr} ${currency} vs imported ${originalTotal.toFixed(2)} ${currency})`,
      );
      continue;
    }

    // 4. Real execution: two-step amend to pin the last night's value.
    //
    // Apaleo's amend endpoint re-derives `totalGrossAmount` from
    // `baseAmount.netAmount` + city tax. When the net is not a clean
    // 2-dp value (e.g. 99.51 / 1.07 = 93.0099...), Apaleo rounds the
    // net to 93.01 and recomputes the gross as 99.52 — silently
    // introducing a ±1 cent drift on the last night.
    //
    // The two-step pattern works around this:
    //   Step 1: Send the DIVISOR-reduced prices. Apaleo stores the
    //           gross but may re-round the net, causing a 1-cent drift.
    //   Step 2: Re-fetch the reservation. If the last night's gross
    //           differs from what we sent by exactly 1 cent, send a
    //           corrective amend with the last night's netAmount set
    //           to the clean value (e.g. 93.00 instead of 93.01) and
    //           grossAmount set to the corrected value (e.g. 99.51
    //           instead of 99.52). Apaleo honors the explicit net on
    //           the second amend because the net→gross derivation
    //           produces a value that can be stored without further
    //           re-rounding.
    try {
      // Two-step amend with drift correction (shared helper — also used by
      // splitHrsClubRates). Reduces per-night accommodation to
      // newPerNightPrices and lets Apaleo re-derive city tax server-side.
      await this._amendAccommodationPrices(
        res,
        newPerNightPrices,
        originalTotal,
        currency,
        log,
      );

      // Leave a trace in the reservation comment so front desk can see the
      // automation ran (read-modify-write; never overwrites, never throws —
      // the money amend already succeeded). German date (dd.mm.yy) + the
      // operator's initials suffix — the marker regex matches SUBSTRINGS,
      // so a trailing " – CK" cannot break the idempotency gate.
      await this._appendReservationComment(
        res.id || "",
        this._automationMarkerLine("recalcCityTax.automationComment", user),
        log,
      );

      const deltaCents = Math.round(
        (projectedNewTotal - originalTotal) * 100,
      );
      const deltaStr =
        deltaCents > 0
          ? `+${(deltaCents / 100).toFixed(2)}`
          : (deltaCents / 100).toFixed(2);
      log(
        "log",
        `Final ${res.id}: Δ ${deltaStr} ${currency} vs imported ${originalTotal.toFixed(2)} ${currency}`,
      );

      result.processed++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "processed",
        originalTotal,
        newTotal: projectedNewTotal,
        nights: timeSlices.length,
        perNightGross: slicePrices,
        perNightAccommodation: newPerNightPrices,
        perNightCityTax: projectedCityTaxPerNight,
        totalAccommodation: Number(totalNewAccommodation.toFixed(2)),
        totalCityTax: Number(totalProjectedCityTax.toFixed(2)),
        currency,
      });
    } catch (err) {
      result.failed++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "failed",
        reason: errorMessage(err) || "unknown_error",
        originalTotal,
        newTotal: projectedNewTotal,
        nights: timeSlices.length,
        perNightGross: slicePrices,
        perNightAccommodation: newPerNightPrices,
        perNightCityTax: projectedCityTaxPerNight,
        totalAccommodation: Number(totalNewAccommodation.toFixed(2)),
        totalCityTax: Number(totalProjectedCityTax.toFixed(2)),
        currency,
      });
      log("error", `Failed ${res.id} (${guestName}): ${errorMessage(err) || err}`);
    }
  }

  if (typeof onProgress === "function") {
    try { onProgress({ processed: total, total, current: null }); } catch { /* noop */ }
  }

  log(
    "log",
    `Done. processed=${result.processed} skipped=${result.skipped} failed=${result.failed} (dryRun=${dryRun})`,
  );

  return result;
},

};

export default part;
